import { useEffect, useState } from 'react';
import {
  Image,
  Modal,
  Platform,
  Pressable,
  Text,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { openExternalUrl } from '../lib/utils';

const MOBILE_APP_LINKS = {
  ios: 'https://apps.apple.com/us/app/per-diem-path-to-fi/id6749600123',
  android: 'https://play.google.com/store/apps/details?id=com.dadmakefire.perdiem',
} as const;

const STORE_BADGES = {
  ios: require('../assets/store/badges/app-store-badge.png'),
  android: require('../assets/store/badges/google-play-badge.png'),
} as const;

const DISMISS_STORAGE_KEY = 'perdiem.mobile-app-interstitial.dismissedUntil';
const DISMISS_COOLDOWN_MS = 1000 * 60 * 60 * 24 * 30;

type InstallTarget = 'ios' | 'android';

type MobileAppInterstitialProps = {
  isDemo: boolean;
  isDesktop: boolean;
  pathname: string | null;
};

function getWebStorage(): Storage | null {
  if (Platform.OS !== 'web' || typeof localStorage === 'undefined') {
    return null;
  }

  if (
    typeof localStorage.getItem !== 'function' ||
    typeof localStorage.setItem !== 'function'
  ) {
    return null;
  }

  return localStorage;
}

function detectInstallTarget(): InstallTarget | null {
  if (Platform.OS !== 'web' || typeof navigator === 'undefined') {
    return null;
  }

  const userAgent = navigator.userAgent ?? '';
  if (/Android/i.test(userAgent)) {
    return 'android';
  }

  const isIOS =
    /\b(iPhone|iPad|iPod)\b/i.test(userAgent) ||
    (/Macintosh/i.test(userAgent) &&
      typeof document !== 'undefined' &&
      'ontouchend' in document);

  return isIOS ? 'ios' : null;
}

function readDismissedUntil(): number {
  const storage = getWebStorage();
  if (!storage) return 0;

  const rawValue = storage.getItem(DISMISS_STORAGE_KEY);
  if (!rawValue) return 0;

  const parsedValue = Number(rawValue);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
}

function persistDismissedUntil(timestamp: number): void {
  const storage = getWebStorage();
  if (!storage) return;

  storage.setItem(DISMISS_STORAGE_KEY, String(timestamp));
}

export function MobileAppInterstitial({
  isDemo,
  isDesktop,
  pathname,
}: MobileAppInterstitialProps) {
  const [installTarget, setInstallTarget] = useState<InstallTarget | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const isDashboardPath = pathname === '/' || pathname === '/(app)';

    if (Platform.OS !== 'web' || isDesktop || isDemo || !isDashboardPath) {
      setIsVisible(false);
      return;
    }

    const nextInstallTarget = detectInstallTarget();
    if (!nextInstallTarget) {
      setIsVisible(false);
      return;
    }

    setInstallTarget(nextInstallTarget);

    if (readDismissedUntil() > Date.now()) {
      setIsVisible(false);
      return;
    }

    setIsVisible(true);
  }, [isDemo, isDesktop, pathname]);

  if (!installTarget) {
    return null;
  }

  const handleInstall = () => {
    openExternalUrl(MOBILE_APP_LINKS[installTarget]);
    setIsVisible(false);
  };

  const handleDismiss = () => {
    setIsVisible(false);
  };

  const handleDismissForAwhile = () => {
    persistDismissedUntil(Date.now() + DISMISS_COOLDOWN_MS);
    setIsVisible(false);
  };

  const subtitle =
    installTarget === 'ios'
      ? 'You already finished setup. The iPhone app is the smoother way to check your per diem, transactions, and FIRE plan on the go.'
      : 'You already finished setup. The Android app is the smoother way to check your per diem, transactions, and FIRE plan on the go.';

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="fade"
      onRequestClose={handleDismiss}
    >
      <Pressable
        className="flex-1 bg-black/70 px-4 py-8"
        onPress={handleDismiss}
      >
        <View className="mx-auto w-full max-w-md flex-1 justify-center">
          <Pressable
            className="rounded-[28px] border border-stone-800 bg-stone-950 p-6"
            onPress={(event) => event.stopPropagation()}
          >
            <View className="flex-row items-start justify-between gap-4">
              <View className="flex-1">
                <Text className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">
                  Prefer the native app?
                </Text>
                <Text className="mt-3 text-2xl font-semibold text-stone-50">
                  Install Per Diem on this device.
                </Text>
              </View>
              <Pressable onPress={handleDismiss} hitSlop={8}>
                <Ionicons name="close" size={18} color="#a8a29e" />
              </Pressable>
            </View>

            <Text className="mt-3 text-sm leading-6 text-stone-300">
              {subtitle}
            </Text>

            <Pressable onPress={handleInstall} className="mt-5 self-start">
              <Image
                source={STORE_BADGES[installTarget]}
                accessibilityLabel={
                  installTarget === 'ios'
                    ? 'Download on the App Store'
                    : 'Get it on Google Play'
                }
                style={
                  installTarget === 'ios'
                    ? { width: 120, height: 40 }
                    : { width: 135, height: 40 }
                }
                resizeMode="contain"
              />
            </Pressable>

            <View className="mt-5 flex-row flex-wrap items-center gap-x-4 gap-y-3">
              <Pressable onPress={handleDismiss}>
                <Text className="text-sm font-medium text-stone-200">Not now</Text>
              </Pressable>
              <Pressable onPress={handleDismissForAwhile}>
                <Text className="text-sm text-stone-500">
                  Don&apos;t show this again for awhile
                </Text>
              </Pressable>
            </View>
          </Pressable>
        </View>
      </Pressable>
    </Modal>
  );
}
